import { Cell, Pie, PieChart } from "recharts";
import { Skeleton } from "@/components/kit";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import type { CustomerRiskTierSummaryDTO } from "@/modules/reports/domain/types";
import { customerRiskTierMeta } from "@/modules/customers/presentation/ui/customer-risk";
import { formatLak } from "@/shared/lib/currency";

const chartConfig = {
  allowCod: { label: "COD ໄດ້", color: "var(--chart-2)" },
  requireDeposit: { label: "ມັດຈຳ", color: "var(--chart-3)" },
  prepaidOnly: { label: "ໂອນກ່ອນ", color: "var(--chart-4)" },
  unknown: { label: "ບໍ່ຮູ້", color: "var(--chart-5)" },
} satisfies ChartConfig;

type CustomerRiskTierChartProps = {
  summary?: CustomerRiskTierSummaryDTO;
  isLoading?: boolean;
};

export function CustomerRiskTierChart({
  summary,
  isLoading,
}: CustomerRiskTierChartProps) {
  if (isLoading) {
    return <Skeleton className="h-60 w-full rounded-2xl" />;
  }

  const data = [
    {
      key: "allowCod",
      value: summary?.allowCod ?? 0,
      fill: "var(--color-allowCod)",
      label: customerRiskTierMeta("allow_cod").shortLabel,
    },
    {
      key: "requireDeposit",
      value: summary?.requireDeposit ?? 0,
      fill: "var(--color-requireDeposit)",
      label: customerRiskTierMeta("require_deposit").shortLabel,
    },
    {
      key: "prepaidOnly",
      value: summary?.prepaidOnly ?? 0,
      fill: "var(--color-prepaidOnly)",
      label: customerRiskTierMeta("prepaid_only").shortLabel,
    },
    {
      key: "unknown",
      value: summary?.unknown ?? 0,
      fill: "var(--color-unknown)",
      label: "ບໍ່ຮູ້",
    },
  ].filter((row) => row.value > 0);

  const total = data.reduce((sum, row) => sum + row.value, 0);

  if (total === 0) {
    return (
      <div className="brand-card flex h-60 items-center justify-center p-6 text-muted-foreground text-sm">
        ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້
      </div>
    );
  }

  return (
    <div className="brand-card min-w-0 overflow-hidden p-4 sm:p-5">
      <div className="mb-2 flex flex-col gap-1">
        <h2 className="font-display text-lg">ສັດສ່ວນຄວາມເສີ່ງ COD</h2>
        <p className="text-muted-foreground text-sm">
          ຄ່າສົ່ງເສຍຕີກັບ {formatLak(summary?.totalReturnLoss ?? "0")} · ລວມ{" "}
          {summary?.sampledCustomers ?? 0} ຄົນ
        </p>
        {summary?.limitedToTop ? (
          <p className="text-muted-foreground text-xs">
            ຄິດໄລ່ຈາກ top 200 ລູກຄ້າ (ທັງໝົດ {summary.totalActiveCustomers})
          </p>
        ) : null}
      </div>
      <details className="group md:open">
        <summary className="mb-3 flex cursor-pointer select-none list-none items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 font-medium text-sm md:hidden [&::-webkit-details-marker]:hidden">
          <span className="group-open:hidden">ເບິ່ງກຣາຟ tier</span>
          <span className="hidden group-open:inline">ເຊື່ອງກຣາຟ</span>
        </summary>
        <ChartContainer
          config={chartConfig}
          className="mx-auto aspect-square h-[220px] max-w-sm"
        >
          <PieChart>
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value, _name, item) => (
                    <span className="font-medium tabular-nums">
                      {value} ຄົນ (
                      {total > 0
                        ? ((Number(value) / total) * 100).toFixed(0)
                        : 0}
                      %)
                    </span>
                  )}
                  labelFormatter={(_label, payload) =>
                    payload?.[0]?.payload?.label ?? _label
                  }
                />
              }
            />
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius={52}
              outerRadius={80}
              paddingAngle={2}
            >
              {data.map((entry) => (
                <Cell key={entry.key} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
      </details>
      <div className="hidden md:block">
        <ChartContainer
          config={chartConfig}
          className="mx-auto aspect-square h-[220px] max-w-sm"
        >
          <PieChart>
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value) => (
                    <span className="font-medium tabular-nums">
                      {value} ຄົນ (
                      {total > 0
                        ? ((Number(value) / total) * 100).toFixed(0)
                        : 0}
                      %)
                    </span>
                  )}
                  labelFormatter={(_label, payload) =>
                    payload?.[0]?.payload?.label ?? _label
                  }
                />
              }
            />
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius={52}
              outerRadius={80}
              paddingAngle={2}
            >
              {data.map((entry) => (
                <Cell key={entry.key} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
      </div>
    </div>
  );
}
